import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';

/**
 * A custom element that renders a paginated collection list with load more button.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [grid] - The grid element.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLButtonElement} [loadMoreButton] - The load more button.
 * @property {HTMLElement} [progressText] - The progress text element.
 * @property {HTMLElement} [progressBar] - The progress bar fill element.
 * @property {HTMLElement} [pagination] - The pagination wrapper.
 *
 * @extends Component<Refs>
 */
export default class CollectionList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {boolean} */
  isLoading = false;

  connectedCallback() {
    super.connectedCallback();

    const { loadMoreButton } = this.refs;

    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', this.#handleLoadMore);
    }

    // Pre-fetch next page
    this.#fetchNextPage();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { loadMoreButton } = this.refs;
    if (loadMoreButton) {
      loadMoreButton.removeEventListener('click', this.#handleLoadMore);
    }
  }

  /**
   * Handle load more button click
   */
  #handleLoadMore = async () => {
    if (this.isLoading) return;

    this.isLoading = true;
    const { loadMoreButton } = this.refs;

    if (loadMoreButton) {
      loadMoreButton.setAttribute('aria-busy', 'true');
      loadMoreButton.disabled = true;
    }

    await this.#renderNextPage();

    this.isLoading = false;

    if (loadMoreButton) {
      loadMoreButton.removeAttribute('aria-busy');
      loadMoreButton.disabled = false;
    }

    // Pre-fetch the following page
    requestIdleCallback(() => {
      this.#fetchNextPage();
    });
  };

  /**
   * Get next page info
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextPage() {
    const { cards, grid } = this.refs;

    if (!Array.isArray(cards) || !grid) return;

    const lastCard = cards[cards.length - 1];
    if (!lastCard) return;

    const currentPage = Number(lastCard.dataset.page);
    const lastPage = Number(grid.dataset.lastPage);
    const nextPage = currentPage + 1;

    if (nextPage > lastPage) return;

    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage.toString());
    url.hash = '';

    return { page: nextPage, url };
  }

  /**
   * Fetch next page content
   */
  async #fetchNextPage() {
    const nextPage = this.#getNextPage();
    if (!nextPage) return;

    if (this.pages.has(nextPage.page)) return;

    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, nextPage.url);
    this.pages.set(nextPage.page, pageContent);
  }

  /**
   * Render next page items
   */
  async #renderNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.#getNextPage();
    if (!nextPage) return;

    let nextPageItems = this.#getGridItemsForPage(nextPage.page);

    if (!nextPageItems) {
      await this.#fetchNextPage();
      nextPageItems = this.#getGridItemsForPage(nextPage.page);
      if (!nextPageItems) return;
    }

    // Append new items to grid
    grid.append(...nextPageItems);

    // Update progress
    this.#updateProgress();

    // Update URL
    history.pushState('', '', nextPage.url.toString());

    // Check if we need to hide the button
    this.#checkHideLoadMore();
  }

  /**
   * Get grid items from cached page HTML
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridItemsForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;

    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Update progress indicator
   */
  #updateProgress() {
    const { progressText, progressBar, grid, pagination } = this.refs;

    if (!grid) return;

    const totalItems = Number(grid.dataset.totalItems) || 0;

    // Count actual visible items
    const visibleItems = grid.querySelectorAll('[ref="cards[]"]').length;

    if (progressText && pagination) {
      const template = pagination.dataset.progressTemplate || 'Viděli jste [count] z [total] kolekcí';
      progressText.textContent = template
        .replace('[count]', String(visibleItems))
        .replace('[total]', String(totalItems));
    }

    if (progressBar) {
      const percentage = totalItems > 0 ? (visibleItems / totalItems) * 100 : 0;
      progressBar.style.width = `${percentage}%`;
    }
  }

  /**
   * Check if load more button should be hidden
   */
  #checkHideLoadMore() {
    const { grid, pagination } = this.refs;
    if (!grid || !pagination) return;

    const totalItems = Number(grid.dataset.totalItems) || 0;
    const visibleItems = grid.querySelectorAll('[ref="cards[]"]').length;

    if (visibleItems >= totalItems) {
      pagination.style.display = 'none';
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }
}

if (!customElements.get('collection-list')) {
  customElements.define('collection-list', CollectionList);
}
